import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FileText, CalendarDays, Megaphone, Inbox, Image as ImageIcon,
  ArrowUpRight, Plus, Edit3, Globe, Activity,
} from 'lucide-react';
import api from '../api';

const quickLinks = [
  { to: '/admin/pages', icon: FileText, label: 'Edit Pages', desc: 'Update website copy and images', color: 'coral' },
  { to: '/admin/events', icon: CalendarDays, label: 'Manage Events', desc: 'Add or edit upcoming events', color: 'teal' },
  { to: '/admin/news', icon: Megaphone, label: 'Post Announcement', desc: 'Share news & updates', color: 'yellow' },
  { to: '/admin/forms', icon: Inbox, label: 'Form Submissions', desc: 'View rental & contact inquiries', color: 'coral' },
];

const colorMap = {
  coral: { bg: 'bg-[#E5614C]', soft: 'bg-[#E5614C]/15', text: 'text-[#E5614C]' },
  teal: { bg: 'bg-[#5DCAB5]', soft: 'bg-[#5DCAB5]/15', text: 'text-[#5DCAB5]' },
  yellow: { bg: 'bg-[#FCD968]', soft: 'bg-[#FCD968]/20', text: 'text-[#FCD968]' },
};

export default function Dashboard() {
  const [stats, setStats] = useState({ pages: 0, events: 0, submissions: 0 });
  const [recentPages, setRecentPages] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [pagesRes, eventsRes, formsStatsRes] = await Promise.all([
          api.get('/pages'),
          api.get('/events').catch(() => ({ data: [] })),
          api.get('/forms/stats').catch(() => ({ data: { total: 0, unread: 0 } })),
        ]);
        const sorted = [...pagesRes.data].sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0));
        setRecentPages(sorted.slice(0, 5));
        setStats({
          pages: pagesRes.data.length,
          events: eventsRes.data.length,
          submissions: formsStatsRes.data.total || 0,
          unread: formsStatsRes.data.unread || 0,
        });
      } catch (e) { /* ignore */ }
    })();
  }, []);

  const statCards = [
    { label: 'Pages', value: stats.pages, icon: FileText, color: 'coral' },
    { label: 'Events', value: stats.events, icon: CalendarDays, color: 'teal' },
    { label: stats.unread ? `Submissions (${stats.unread} unread)` : 'Submissions', value: stats.submissions, icon: Inbox, color: 'yellow' },
  ];

  return (
    <div className="max-w-7xl space-y-8">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="inline-flex items-center gap-2 text-[#E5614C] font-semibold uppercase tracking-[0.18em] text-[11px]">
            <Activity className="h-3 w-3" /> Dashboard
          </div>
          <h1 className="font-serif-display text-[36px] lg:text-[42px] font-bold mt-2">Site overview</h1>
          <p className="text-white/55 text-[14px] mt-1">Manage your website content, events, and inquiries.</p>
        </div>
        <Link to="/" target="_blank" className="inline-flex items-center gap-2 h-10 px-5 rounded-full bg-white/5 border border-white/10 text-[13px] font-semibold hover:bg-white/10 transition-colors">
          <Globe className="h-3.5 w-3.5" /> View live site <ArrowUpRight className="h-3.5 w-3.5" />
        </Link>
      </div>

      {/* Stat cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {statCards.map((s) => {
          const cl = colorMap[s.color];
          return (
            <div key={s.label} className="rounded-2xl bg-[#16131a] border border-white/[0.06] p-6 relative overflow-hidden hover:border-white/15 transition-colors">
              <div className={`absolute -top-12 -right-12 w-32 h-32 rounded-full opacity-50 ${cl.soft}`} />
              <div className="relative flex items-start justify-between">
                <div>
                  <div className="text-white/55 text-[12px] uppercase tracking-[0.16em] font-semibold">{s.label}</div>
                  <div className="font-serif-display text-[44px] font-bold mt-2 leading-none">{s.value}</div>
                </div>
                <div className={`h-11 w-11 rounded-xl ${cl.bg} ${s.color === 'yellow' ? 'text-[#2A1B1A]' : 'text-white'} flex items-center justify-center`}>
                  <s.icon className="h-5 w-5" />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Quick links */}
      <div>
        <h2 className="font-serif-display text-[22px] font-bold mb-4">Quick actions</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          {quickLinks.map((l) => {
            const cl = colorMap[l.color];
            const inner = (
              <div className="group relative rounded-2xl bg-[#16131a] border border-white/[0.06] p-6 hover:border-white/20 hover:-translate-y-0.5 transition-[transform,border-color] duration-300 overflow-hidden">
                <div className={`absolute -top-14 -right-14 w-32 h-32 rounded-full opacity-0 group-hover:opacity-80 transition-opacity duration-500 ${cl.soft}`} />
                <div className="relative flex items-start gap-4">
                  <div className={`h-12 w-12 rounded-xl ${cl.bg} ${l.color === 'yellow' ? 'text-[#2A1B1A]' : 'text-white'} flex items-center justify-center shrink-0`}>
                    <l.icon className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="font-semibold text-[16px]">{l.label}</div>
                      {l.soon && <span className="text-[9px] font-bold tracking-[0.14em] uppercase px-1.5 py-0.5 rounded bg-white/10 text-white/55">Soon</span>}
                    </div>
                    <div className="text-white/55 text-[13px] mt-1">{l.desc}</div>
                  </div>
                  <ArrowUpRight className="h-4 w-4 text-white/40 group-hover:text-white group-hover:translate-x-1 group-hover:-translate-y-1 transition-[transform,color] shrink-0" />
                </div>
              </div>
            );
            return l.soon ? (
              <div key={l.to} className="cursor-not-allowed opacity-70">{inner}</div>
            ) : (
              <Link key={l.to} to={l.to}>{inner}</Link>
            );
          })}
        </div>
      </div>

      {/* Recently updated */}
      <div>
        <h2 className="font-serif-display text-[22px] font-bold mb-4">Recently updated pages</h2>
        <div className="rounded-2xl bg-[#16131a] border border-white/[0.06] overflow-hidden">
          {recentPages.length === 0 && (
            <div className="p-8 text-center text-white/50 text-[14px]">No pages yet.</div>
          )}
          {recentPages.map((p, i) => (
            <Link
              key={p.slug} to={`/admin/pages/${p.slug}`}
              className={`flex items-center justify-between gap-4 px-6 py-4 hover:bg-white/[0.03] transition-colors ${i < recentPages.length - 1 ? 'border-b border-white/[0.06]' : ''}`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-10 w-10 rounded-lg bg-[#E5614C]/10 text-[#E5614C] flex items-center justify-center">
                  <FileText className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <div className="font-semibold text-[15px] truncate">{p.title}</div>
                  <div className="text-[12px] text-white/45 truncate">{p.route} · updated {formatDate(p.updated_at)}</div>
                </div>
              </div>
              <Edit3 className="h-4 w-4 text-white/40 shrink-0" />
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

function formatDate(d) {
  if (!d) return 'recently';
  const dt = new Date(d);
  const diff = (Date.now() - dt.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return dt.toLocaleDateString();
}
